import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { GenericRadioCard } from "@ui-components/RadioCard/GenericRadioCard";
import LastMileConfiguration from "./LastMileConfiguration";
import { fetchCrossPricing, updateOrder } from "@redux/slices/kontrolEdge/orderSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@redux/store/store";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import GenericInput from "@ui-components/Input/GenericInput";
import GenericCheckbox from "@ui-components/Checkbox/GenericCheckbox";
import { useAppSelector } from "@redux/hooks";
import { ReactComponent as IconInformation } from "../../../../shared/assets/Icons/Iconinformation.svg";

export interface PortConfigurationForm {
  portName: string;
  portTag: string;
  automaticVlan: boolean;
  vlan: string;
  crossConnect: string;
  crossPricing: string;
  address?: {
    street: string;
    city: string;
    state: string;
    country: string;
    postalCode: string;
  };
}

export interface PortConfigurationRef {
  submit: () => Promise<boolean>;
}

const PortConfiguration = forwardRef<PortConfigurationRef, { token: string | null }>(
  ({ token }, ref) => {
  const selectedPortIndex = useAppSelector((state) => state.order.selectedPortIndex) || 0;
  const savedPort = useAppSelector((state) => state.order.ports[selectedPortIndex]);
  const crossPricingData = useSelector((state: RootState) => {
    const port = state.order.ports[state.order.selectedPortIndex ?? 0];
    return port?.configuration?.crossConnectPricing;
  });

  const { 
    handleSubmit,
    control,
    reset,
    formState: { errors },
    watch
  } = useForm<PortConfigurationForm>({
    defaultValues: {
      portName: "",
      portTag: "",
      automaticVlan: false,
      vlan: "",
      crossConnect: "provideXC",
      address: {
        street: "",
        city: "",
        state: "",
        country: "",
        postalCode: "",
      },
    },
  });

  const hasReset = useRef(false);

  useEffect(() => {
    if (savedPort && !hasReset.current) {
      reset({
        portName: savedPort.basicInfo?.name || "",
        portTag: savedPort.basicInfo?.tag || "",
        automaticVlan: savedPort.configuration?.automaticVlan || false,
        vlan: savedPort.configuration?.vlan || "",
        crossConnect: savedPort.configuration?.crossConnect || "provideXC",
        address: savedPort.configuration?.address || {
          street: "",
          city: "",
          state: "",
          country: "",
          postalCode: "",
        },
      });
      hasReset.current = true;
    }
  }, [savedPort, reset]);

  
  const dispatch = useDispatch<AppDispatch>();
  const automaticVlan = watch("automaticVlan");
  const selectedOption = watch("crossConnect");
  const [address, setAddress] = useState({
    street: "",
    city: "",
    state: "",
    country: "",
    postalCode: "",
  });

  const crossConnectValue = watch("crossConnect");
  useEffect(() => {
    // Update Redux with the current crossConnect selection
    dispatch(
      updateOrder({
        portIndex: 0,
        portData: {
          configuration: {
            crossConnect: crossConnectValue,
          },
        },
      })
    );
    if (crossConnectValue === "requireXC" && token) {
      dispatch(fetchCrossPricing({ token, provider: "1" }));
    }
  }, [crossConnectValue, token, dispatch]);
  


  useEffect(() => {
    if (selectedOption === "requireXC" && token) {
      dispatch(fetchCrossPricing({ token, provider: "1" }));
    }
  }, [selectedOption, token, dispatch]);

  useEffect(() => {
    console.log('crossPricingData:', crossPricingData);
  }, [crossPricingData]);


  const onSubmit: SubmitHandler<PortConfigurationForm> = (data) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const portData: Partial<any> = {
      basicInfo: {
        name: data.portName,
        tag: data.portTag,
      },
      configuration: {
        vlan: data.vlan,
        automaticVlan: data.automaticVlan,
        crossConnect: data.crossConnect,
        address: selectedOption === "lastMile" ? address : undefined,
      },
    };
    dispatch(updateOrder({ portIndex: 0, portData }));
  };

  // Expose the submit method to the parent
  useImperativeHandle(ref, () => ({
    submit: async () => {
      return new Promise<boolean>((resolve) => {
        handleSubmit(
          (data) => {
            onSubmit(data);
            resolve(true);
          },
          () => {
            resolve(false);
          }
        )();
      });
    },
  }));

  const contractOptions = [
    {
      value: "provideXC",
      label: "I will provide Cross Connect (XC)",
      toolText: "This is a personalized text for provide cross connect",
    },
    {
      value: "requireXC",
      label: "I need Cross Connect (XC)",
      toolText: "This is a personalized text for need cross connect",
    },
    {
      value: "lastMile",
      label: "I need Last Mile - Price will be estimated",
      toolText: "This is a personalized text for last mile",
    },
  ];
    

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="px-7 pt-5">
        <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
          Details
        </h3>

        {/* Port Name Input */}
        <Controller
          name="portName"
          control={control}
          rules={{ required: "Port Name is required" }}
          render={({ field }) => (
            <GenericInput
              label="Port Name"
              placeholder="Enter Port Name"
              required
              value={field.value}
              onChange={field.onChange}
              hasError={!!errors.portName}
              helperText={errors.portName?.message}
              className="mb-5"
            />
          )}
        />
        {/* Port Tag Input */}
        <Controller
          name="portTag"
          control={control}
          render={({ field }) => (
            <GenericInput
              label="Tag"
              placeholder="#Project Diamond"
              value={field.value ?? ""}
              onChange={field.onChange}
              hasError={!!errors.portTag}
              helperText={errors.portTag?.message}
            />
          )}
        />

        <div className="mt-5 w-80% border-b border-gray-300" />
      </div>

      <div className="px-7 pt-5">
        <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
          VLAN Configuration
        </h3>

        {/* Automatic VLAN Assigned Checkbox */}
        <Controller
          name="automaticVlan"
          control={control}
          render={({ field }) => (
            <GenericCheckbox
              label="Automatic VLAN assigned"
              checked={field.value}
              onChange={field.onChange}
              className="mb-5"
            />
          )}
        />

        {/* VLAN Range Input */}
        <Controller
          name="vlan"
          control={control}
          rules={{
            required: !automaticVlan ? "VLAN number is required" : false,
          }}
          render={({ field }) => (
            <GenericInput
              label="VLAN Number"
              type="number"
              placeholder="Enter VLAN number"
              value={field.value}
              onChange={field.onChange}
              disabled={automaticVlan}
              required={!automaticVlan}
              hasError={!!errors.vlan}
              helperText={errors.vlan?.message}
              tooltipText="Range VLAN 100-4500"
              tooltipIcon={<IconInformation />}
            />
          )}
        />
        <div className="mt-5 w-80% border-b border-gray-300" />
      </div>

      <div className="px-7 pt-5">
        <h3 className="text-2l font-bold mb-4 text-left text-gray-400">
          Cross Connect and Last Mile
        </h3>

        {/* Cross Connect Radio Buttons */}
        <Controller
          name="crossConnect"
          control={control}
          rules={{ required: "Please select a Cross Connect option" }}
          render={({ field }) => (
            <div className="space-y-3">
              {contractOptions.map((option) => (
                <GenericRadioCard
                  key={option.value}
                  name={field.name}
                  value={option.value}
                  label={option.label}
                  checked={field.value === option.value}
                  onChange={field.onChange}
                  tooltipText={option.toolText}
                  tooltipPosition="right"
                />
              ))}
            </div>
          )}
        />
        {errors.crossConnect && (
          <p className="text-red-500 text-sm">
            {errors.crossConnect.message}
          </p>
        )}
        {/* Display API Cross*/}
        {selectedOption === "requireXC" && (
          <div className="flex mt-4 justify-center">
            {crossPricingData && (
              <GenericTag 
                label={`$ ${crossPricingData} USD / Month`}
                variant="success"
              />
            )}
          </div>
        )}
        {/* Address Section for Last Mile */}
        {selectedOption === "lastMile" && (
          <LastMileConfiguration
            address={address}
            onAddressChange={setAddress}
          />
        )}
        <div className="mt-5 w-80% border-b border-gray-300" />
      </div>
    </form>
  );
});

PortConfiguration.displayName = "PortConfiguration";
export default PortConfiguration;
